/* eslint-disable indent */
/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		// default date format in DayJS format
		this.defaultDateFormat = 'YYYY-MM-DD';
	}

	getDateHiddenInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}

	getDateDisplayInputHTMLID(pInputHTMLID)
	{
		return `#DATE-INPUT-FOR-${pInputHTMLID}`;
	}

	getTabularDateHiddenInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#DATE-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularDateDisplayInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#DATE-TABULAR-INPUT-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Initialize the Flatpickr plugin for the input element.
	 * @param {object} pInput The input object.
	 * @param {string} pValue The the date value in ISO format.
	 * @param {boolean} isTabular Whether the input is tabular.
	 * @param {number} pRowIndex The row index
	 */
	configureFlatpickr(pInput, pValue, isTabular = false, pRowIndex)
	{
		if (!this.pict.AppData.Interactive)
		{
			return;
		}
		// NOTE: pValue is expected to be in ISO format (e.g., "2024-06-01T00:00:00Z") for proper timezone handling
		this.pict.providers.FlatpickrUtilProvider.initializeFlatpickr({
			PickerType: 'date',
			AllowInput: true,
			HTMLID: isTabular ? this.getTabularDateDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex) : this.getDateDisplayInputHTMLID(pInput.Macro.RawHTMLID),
			ISODateString: pValue,
			Timezone: pInput.PictForm.Timezone || this.fable.options.Timezone,
			DayJSDateFormat: pInput.PictForm.DateFormat || this.defaultDateFormat, // ex: 'YYYY-MM-DD'
			DefaultValue: pInput.Default,
			DisablePicker: pInput.PictForm.Disabled || false,
		});
	}

	/**
	 * Resolve a logical default token ('now'/'today') carried in the marshaled model
	 * value to a concrete ISO string and lock that value into the form's data model, so
	 * a defaulted-but-untouched field persists the instant it was created instead of
	 * re-evaluating live on every reload. Returns the (possibly resolved) value to use
	 * for the rest of the marshal. Non-token values pass through untouched.
	 *
	 * @param {Object} pView - The dynamic form view.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The marshaled model value.
	 * @param {number} [pRowIndex] - Row index when marshaling a tabular cell.
	 * @return {any} The value to continue the marshal with (resolved ISO for tokens).
	 */
	lockInSpecialDefault(pView, pInput, pValue, pRowIndex)
	{
		const tmpFlatpickr = this.pict.providers.FlatpickrUtilProvider;
		if (!tmpFlatpickr.isSpecialDefaultValue(pValue))
		{
			return pValue;
		}
		const tmpTimezone = pInput.PictForm.Timezone || this.fable.options.Timezone;
		const tmpResolvedISO = tmpFlatpickr.resolveSpecialDefaultToISO(pValue, tmpTimezone, 'date');
		if (pView && typeof pView.getMarshalDestinationObject === 'function' && pView.sectionManifest)
		{
			let tmpAddress = pInput.Hash;
			if (pRowIndex != null)
			{
				tmpAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
			}
			pView.sectionManifest.setValueByHash(pView.getMarshalDestinationObject(), tmpAddress, tmpResolvedISO);
		}
		return tmpResolvedISO;
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		let tmpValue = !pValue || pValue == 0 ? '' : pValue;
		tmpValue = this.lockInSpecialDefault(pView, pInput, tmpValue);
		const tmpDisplayDate = this.pict.providers.FlatpickrUtilProvider.formatDateWithTimezone(tmpValue, pInput.PictForm.DateFormat || this.defaultDateFormat);
		this.pict.ContentAssignment.assignContent(this.getDateHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpValue);
		this.pict.ContentAssignment.assignContent(this.getDateDisplayInputHTMLID(pInput.Macro.RawHTMLID), tmpDisplayDate);
		this.configureFlatpickr(pInput, tmpValue, false, 0);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, tmpValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		let tmpValue = !pValue || pValue == 0 ? '' : pValue;
		tmpValue = this.lockInSpecialDefault(pView, pInput, tmpValue, pRowIndex);
		const tmpDisplayDate = this.pict.providers.FlatpickrUtilProvider.formatDateWithTimezone(tmpValue, pInput.PictForm.DateFormat || this.defaultDateFormat);
		this.pict.ContentAssignment.assignContent(this.getTabularDateHiddenInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpValue);
		this.pict.ContentAssignment.assignContent(this.getTabularDateDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpDisplayDate);
		this.configureFlatpickr(pInput, tmpValue, true, pRowIndex);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, tmpValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		try
		{
			// Read directly from Flatpickr's selectedDates and interpret as document-TZ via fromFlatpickrDate
			const tmpInputElement = document.querySelector(this.getDateDisplayInputHTMLID(pInput.Macro.RawHTMLID));
			const tmpFlatpickrInstance = tmpInputElement?._flatpickr;
			if (tmpFlatpickrInstance && tmpFlatpickrInstance.selectedDates.length > 0)
			{
				const tmpTimezone = pInput.PictForm.Timezone || this.fable.options.Timezone;
				const tmpISOValue = this.pict.providers.FlatpickrUtilProvider.fromFlatpickrDate(
					tmpFlatpickrInstance.selectedDates[0], tmpTimezone, 'date',
				);
				this.pict.ContentAssignment.assignContent(this.getDateHiddenInputHTMLID(pInput.Macro.RawHTMLID), tmpISOValue);
				pView.dataChanged(pInput.Hash);
			}
		}
		catch (pError)
		{
			this.pict.log.error(`Date input error for [#${pInput.Macro.RawHTMLID}]: ${pError.message}`);
		}

		return super.onDataRequest(pView, pInput, pValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		try
		{
			// Read directly from Flatpickr's selectedDates and interpret as document-TZ via fromFlatpickrDate
			const tmpInputElement = document.querySelector(this.getTabularDateDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex));
			const tmpFlatpickrInstance = tmpInputElement?._flatpickr;
			if (tmpFlatpickrInstance && tmpFlatpickrInstance.selectedDates.length > 0)
			{
				const tmpTimezone = pInput.PictForm.Timezone || this.fable.options.Timezone;
				const tmpISOValue = this.pict.providers.FlatpickrUtilProvider.fromFlatpickrDate(
					tmpFlatpickrInstance.selectedDates[0], tmpTimezone, 'date',
				);
				this.pict.ContentAssignment.assignContent(this.getTabularDateHiddenInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpISOValue);
				pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
			}
		}
		catch (pError)
		{
			this.pict.log.error(`Date tabular input error for [#${pInput.Macro.RawHTMLID}]: ${pError.message}`);
		}

		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}
}

module.exports = CustomInputHandler;
